/**
 * The invitation — "would you like a look round?" (TUTORIAL-STATE-001).
 *
 * Reagan, 2026-09-21: *"if a pop up comes up and asks if they want a
 * tutorial"*. This is that pop-up, and the two answers it offers are the whole
 * design:
 *
 *   SHOW ME AROUND   starts the tour
 *   NOT NOW          asked once more next time, then left alone
 *
 * ── TWO BUTTONS, AFTER BEING THREE ─────────────────────────────────────────
 *
 * There was a "No thanks" as well, on the argument that "not now" and "never"
 * are different answers and the dialog should offer the honest one. Reagan,
 * 2026-09-23: *"why does it have no thanks and not now? just needs to be not
 * now"*. He is right, and the reason is in the counter: the invitation stops
 * offering itself after the second "Not now" anyway. The third button asked
 * somebody four seconds into the product to make a permanent decision, to
 * reach an outcome they get by doing nothing twice.
 *
 * ── WHY IT COUNTS ITSELF WHEN IT RENDERS, NOT WHEN THE PAGE LOADS ──────────
 *
 * `offered` is recorded from an effect in this component, so the counter moves
 * only when the invitation is genuinely on screen. Recording it where the state
 * is fetched would spend one of the two offers on a user who closed the tab
 * while the query was in flight.
 *
 * It fires once per mount, guarded by a ref. React 18's development
 * StrictMode double-invokes effects, and without the guard the first render of
 * the invitation would spend BOTH offers at once — a bug that only exists in
 * development, which is exactly the sort that reaches production as a
 * behaviour nobody can reproduce.
 */

import { useEffect, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from './useFocusTrap';

export interface TutorialInviteProps {
  /** First name, for the greeting. Falls back to an unnamed welcome. */
  firstName?: string | null;
  /** Their second and final offer, so the note can say so. */
  isFinalOffer: boolean;
  onStart: () => void;
  onDefer: () => void;
  /** Record that the invitation actually appeared. Called once per mount. */
  onShown: () => void;
}

export function TutorialInvite({
  firstName,
  isFinalOffer,
  onStart,
  onDefer,
  onShown,
}: TutorialInviteProps): JSX.Element {
  const { t } = useTranslation('tutorial');
  const shown = useRef(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    onShown();
  }, [onShown]);

  /*
   * Focus is held here while this is open, so the `aria-modal` below is a fact.
   * It matters more on this dialog than on the tour's own card: this is the
   * very first thing a new user meets, it covers a page they have never seen,
   * and without the trap Tab walked them straight into an application they had
   * just been told was unavailable.
   *
   * Always on — unlike the tour card's, which is gated on `running` — because
   * this component only exists while the invitation is up. No `restoreTo`: the
   * invitation appears on arrival, so there is rarely anything meaningful to
   * restore to, and the hook falls back to whatever had focus.
   */
  useFocusTrap(cardRef, true);

  /*
   * The tour's own overlay and card classes, not `.modal-overlay` / `.modal`.
   *
   * Reagan on the first version, which used them: *"can we make it look more
   * modern this looks 2000s"*. The platform's modal is a square-cornered white
   * panel with a 1px border and a rule above its buttons — correct for the
   * ninety forms it serves, and wrong for the first thing a new user ever sees,
   * where it reads as a system dialog rather than a welcome.
   */
  return (
    <div className="tut-plain" data-testid="tutorial-invite">
      <div
        ref={cardRef}
        className="tut-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tut-invite-title"
        /*
         * Focusable, not tabbable. The trap opens on the card rather than on a
         * button, so the dialog announces itself and nothing destructive is
         * sitting under the first Enter. -1 keeps it out of the Tab ring.
         */
        tabIndex={-1}
      >
        {/*
          * A compass, not a lightbulb — the sidebar's Guidance Engine already
          * owns the bulb, and this is about finding your way rather than about
          * knowing the rules.
          */}
        <div className="tut-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <path d="M15.2 8.8l-2 4.4-4.4 2 2-4.4z" />
          </svg>
        </div>
        <div className="tut-title" id="tut-invite-title">
          {firstName ? t('invite.title', { firstName }) : t('invite.title_no_name')}
        </div>
        <p className="tut-body">{t('invite.body')}</p>
        {/*
          * The note, not a fourth button. It answers the question the buttons
          * raise — "if I say no now, is that it?" — and the answer differs on
          * the last offer, because after this one we stop asking and the only
          * route back is the header.
          */}
        <p className="tut-note" data-testid="tutorial-invite-note">
          {isFinalOffer ? t('invite.final_note') : t('invite.later_note')}
        </p>
        {/*
          * TWO BUTTONS. Reagan, 2026-09-23: *"why does it have no thanks and
          * not now? just needs to be not now"*.
          *
          * There were three, and the third was reasoning rather than a
          * requirement. The argument for it was that "not now" and "never" are
          * different answers and a dialog should offer the honest one. Both
          * true, and both already served: the counter stops the invitation
          * after the second "Not now" without anybody having to say never. So
          * "No thanks" asked the reader to make a permanent decision four
          * seconds into the product, to get an outcome they reach anyway by
          * doing nothing twice.
          *
          * Nothing is lost. `declined` is still a real state — skipping out of
          * a running tour records it — it is just no longer something the
          * invitation asks for.
          */}
        <div className="tut-actions">
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="tut-btn"
              onClick={onDefer}
              data-testid="tutorial-invite-later"
            >
              {t('invite.later')}
            </button>
            <button
              type="button"
              className="tut-btn pri"
              onClick={onStart}
              data-testid="tutorial-invite-start"
            >
              {t('invite.start')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
